import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { AnalyticsWorkerDto, AnalyticsWorkersResponseDto } from "@normax-performance/shared-types";
import { Badge, SortableColumnHeader, TimeDistributionBar, useSortableRows } from "../../../shared-ui/primitives";
import { useLocale } from "../../../i18n/LocaleProvider";
import { formatAnalyticsMinutes, formatAnalyticsPercent, formatAnalyticsTrend } from "../../../lib/format";

export interface WorkersTableProps {
  response: AnalyticsWorkersResponseDto;
}

const ZONE_SUB_LABEL_KEY = { below: "belowStandard", neutral: "inExpectedRange", above: "aboveStandard" } as const;

/**
 * The flagship Analytics table (task: time-distribution simplification §7).
 * Columns: Šifra · Radnik · Ukupno vreme · Raspodela vremena · Učinak ·
 * Kvalifikacija · Trend. "Standardni min." (earned standard minutes) was
 * removed — it is a Method C intermediate, not real recorded time. "Ukupno
 * vreme" = normed + neutral + unnormed; "Raspodela vremena" is ONE compact
 * segmented bar + integer legend, never 3–4 separate wide columns. Time
 * distribution and performance stay two separate concepts, exactly as in the
 * Worker Report. Eligible and non-eligible workers remain two visually
 * separate sections, each independently sortable via `useSortableRows`.
 */
export function WorkersTable({ response }: WorkersTableProps) {
  const { t } = useTranslation("analytics");
  const { intlLocale } = useLocale();

  const columns = {
    code: (w: AnalyticsWorkerDto) => w.employeeCode,
    worker: (w: AnalyticsWorkerDto) => w.displayName,
    // Hidden accessor — no visible "Normirani min" column any more, but the
    // non-eligible section still initial-sorts by it (closest-to-eligible first).
    normedMinutes: (w: AnalyticsWorkerDto) => Number(w.normedMinutes),
    totalRecordedMinutes: (w: AnalyticsWorkerDto) => Number(w.timeDistribution.totalRecordedMinutes),
    performance: (w: AnalyticsWorkerDto) => (w.performancePercentLabel === null ? null : Number(w.performancePercentLabel)),
    trend: (w: AnalyticsWorkerDto) => w.trend.trendPercentagePoints,
  };

  const eligibleSort = useSortableRows(response.eligible, columns, { key: "performance", direction: "desc" });
  const nonEligibleSort = useSortableRows(response.nonEligible, columns, { key: "normedMinutes", direction: "desc" });

  return (
    <div className="nx-analytics-table-wrap nx-scroll-table">
      {response.eligible.length > 0 ? (
        <>
          <p className="nx-analytics__section-title">{t("workers.eligibleSection")}</p>
          <WorkersTableBody
            rows={eligibleSort.sorted}
            sortKey={eligibleSort.sortKey}
            direction={eligibleSort.direction}
            onSort={eligibleSort.requestSort}
            rankingMinimumMinutes={response.rankingMinimumMinutes}
            intlLocale={intlLocale}
            period={response.period}
          />
        </>
      ) : null}

      {response.nonEligible.length > 0 ? (
        <>
          <p className="nx-analytics__section-title">{t("workers.nonEligibleSection")}</p>
          <WorkersTableBody
            rows={nonEligibleSort.sorted}
            sortKey={nonEligibleSort.sortKey}
            direction={nonEligibleSort.direction}
            onSort={nonEligibleSort.requestSort}
            rankingMinimumMinutes={response.rankingMinimumMinutes}
            intlLocale={intlLocale}
            period={response.period}
          />
        </>
      ) : null}
    </div>
  );
}

function WorkersTableBody({
  rows,
  sortKey,
  direction,
  onSort,
  rankingMinimumMinutes,
  intlLocale,
  period,
}: {
  rows: AnalyticsWorkerDto[];
  sortKey: string;
  direction: "asc" | "desc";
  onSort: (key: string) => void;
  rankingMinimumMinutes: number | null;
  intlLocale: string;
  period: AnalyticsWorkersResponseDto["period"];
}) {
  const { t } = useTranslation("analytics");

  const distLabels = {
    normed: t("summary.normedWork"),
    neutral: t("summary.neutralActivities"),
    unnormed: t("summary.unnormedWork"),
  };

  return (
    <table className="nx-analytics-table nx-table" style={{ marginBottom: "var(--space-5)" }}>
      <thead>
        <tr>
          <SortableColumnHeader label={t("workers.code")} sortKey="code" activeSortKey={sortKey} direction={direction} onSort={onSort} />
          <SortableColumnHeader label={t("workers.worker")} sortKey="worker" activeSortKey={sortKey} direction={direction} onSort={onSort} />
          <SortableColumnHeader
            label={t("workers.totalRecordedMinutes")}
            sortKey="totalRecordedMinutes"
            activeSortKey={sortKey}
            direction={direction}
            onSort={onSort}
            className="nx-analytics-table__num"
          />
          <th className="nx-analytics-table__dist-col">{t("workers.timeDistribution")}</th>
          <SortableColumnHeader
            label={t("workers.performance")}
            sortKey="performance"
            activeSortKey={sortKey}
            direction={direction}
            onSort={onSort}
            className="nx-analytics-table__num"
          />
          <th>{t("workers.eligibility")}</th>
          <SortableColumnHeader
            label={t("workers.trend")}
            sortKey="trend"
            activeSortKey={sortKey}
            direction={direction}
            onSort={onSort}
            className="nx-analytics-table__num"
          />
        </tr>
      </thead>
      <tbody>
        {rows.map((w) => {
          const zoneClass = w.zone ? w.zone.zone : null;
          return (
            <tr key={w.workerId}>
              <td>{w.employeeCode ?? "—"}</td>
              <td>
                {w.workerId ? (
                  <Link to={`/izvestaji?type=WORKER&workerId=${w.workerId}&from=${period.from}&to=${period.to}`}>{w.displayName}</Link>
                ) : (
                  w.displayName
                )}
              </td>
              <td className="nx-analytics-table__num">{formatAnalyticsMinutes(w.timeDistribution.totalRecordedMinutes, intlLocale)}</td>
              <td className="nx-analytics-table__dist-col">
                <TimeDistributionBar distribution={w.timeDistribution} intlLocale={intlLocale} variant="compact" labels={distLabels} ariaLabel={t("workers.timeDistribution")} />
              </td>
              <td className="nx-analytics-table__num">
                <span className={`nx-analytics-table__cell-readout${zoneClass ? ` nx-analytics-table__cell-readout--${zoneClass}` : ""}`}>
                  {formatAnalyticsPercent(w.performancePercentLabel, intlLocale)}
                </span>
                {w.zone ? <span className="nx-analytics-table__sub">{t(`workers.${ZONE_SUB_LABEL_KEY[w.zone.zone]}`)}</span> : null}
              </td>
              <td>
                {w.rankingEligible ? (
                  <span className="nx-analytics-table__qualified">{t("workers.eligibleBadge")}</span>
                ) : (
                  <>
                    <Badge tone="neutral">{t("workers.notEligibleBadge")}</Badge>
                    {rankingMinimumMinutes !== null ? (
                      <span className="nx-analytics-table__sub">
                        {t("workers.minutesOfMinimum", {
                          minutes: formatAnalyticsMinutes(w.normedMinutes, intlLocale),
                          minimum: formatAnalyticsMinutes(String(rankingMinimumMinutes), intlLocale),
                        })}
                      </span>
                    ) : null}
                  </>
                )}
              </td>
              <td className={`nx-analytics-table__num nx-analytics__trend-cell nx-analytics__trend-cell--${w.trend.direction ?? "unavailable"}`}>
                {formatAnalyticsTrend(w.trend.trendPercentagePoints)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
